import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, combineLatest, of, ReplaySubject, Subject, Subscription } from "rxjs";
import { switchMap, take } from "rxjs/operators";
import { PaginationPage, PaginationResponse } from "src/app/classes";
import { DoubletSuggestion } from "../../shared/classes";
import { IDoubletProviderService } from "../../shared/interfaces";
import { IDepartmentsProviderService } from "../interfaces";
import { ContactService } from "./contact.service";

@Injectable()
export class CreateContactService implements IDoubletProviderService, IDepartmentsProviderService {

    private _checkerVisible = new BehaviorSubject<boolean>(true);
    checkerVisible$ = this._checkerVisible.asObservable();

    private _doubletClicked = new Subject<DoubletSuggestion>();
    doubletClicked$ = this._doubletClicked.asObservable();

    private _doublets = new ReplaySubject<PaginationResponse<DoubletSuggestion[]>>(1);
    doublets$ = this._doublets.asObservable();

    private _departments = new BehaviorSubject([]);
    departments$ = this._departments.asObservable();

    private _departmentPaginationPage = new BehaviorSubject<PaginationPage>({ 'pageNumber': 1, 'pageSize': 20 });
    private _departmentTotalItems = new BehaviorSubject(0);
    private _hasMoreDepartments = new BehaviorSubject(false);

    departmentPaginationPage$ = this._departmentPaginationPage.asObservable();
    departmentTotalItems$ = this._departmentTotalItems.asObservable();
    hasMoreDepartments$ = this._hasMoreDepartments.asObservable();

    private _subscriptions: Subscription[] = [];

    constructor(
        private _contactService: ContactService,
        private _router: Router
    ) {
        this.setUp();
    }

    dispose() {
        for (let sub of this._subscriptions) sub.unsubscribe();
        this._subscriptions = [];
    }

    doubletClick = (doublet: DoubletSuggestion) => {
        this._doubletClicked.next(doublet);
        this._router.navigate(['/contact-details', doublet.identifier]);
    }

    loadMoreDepartments(): void {
        this.departmentPaginationPage$.pipe(take(1)).subscribe((page: PaginationPage) => {
            page.pageNumber++;
            this._departmentPaginationPage.next(page);
        });
    }

    setSearchFilter() {
        throw new Error("not implemented");
    }

    setSearchString = (term: string) => this._contactService.searchContactsByString(term, { 'pageNumber': 1, 'pageSize': 50 }).subscribe((x => {
        var result = {
            totalItems: x.totalItems,
            content: x.content.map(y => {
                return {
                    title: [y.contactperssalutation, y.contactpersfname, y.contactperslname].filter(z => z ? true : false).join(' '),
                    subTitles: [],
                    identifier: y.contactpersid
                } as DoubletSuggestion
            })
        } as PaginationResponse<DoubletSuggestion[]>;
        this._doublets.next(result);
    }));

    setUp() {
        this._subscriptions.push(...[
            this.departmentPaginationPage$.pipe(switchMap((page: PaginationPage) => this._contactService.getDepartmentSuggestions(page)))
                .pipe(switchMap((result) => combineLatest([of(result), this.departments$]).pipe(take(1))))
                .subscribe(([result, departments]) => {
                    if (result.page.pageNumber === 1) departments = [];
                    departments = [...departments, ...result.content];
                    this._departmentTotalItems.next(result.totalItems);
                    this._departments.next(departments);
                    this._hasMoreDepartments.next(departments.length < result.totalItems);
                })
        ])
    }

    setDoubletCheckerVisible = (value: boolean) => this._checkerVisible.next(value);
}